import { Dispatch, SetStateAction } from 'react';

import {
  FileReportUploadResponse,
  FileReportValidationStatusEnum,
} from '@fora-monorepo/employer-my-pages-common/types';
import {
  Button,
  FAQ,
  Feedback,
  FileDrop,
  Flex,
  Panel,
  PanelStatus,
  QuestionsAndAnswersType,
  Text,
} from '@foradevelopment/fora-ui';

import { FileReportStatusCard } from './FileReportStatusCard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { goToFileUploadPanel, goToReviewAndConfirmPanel } from '../../redux/file/fileSlice';
import { useTypedTranslation } from '../../i18n/i18n';

interface FileReportingUploadPanelProps {
  fileResponse: FileReportUploadResponse | null;
  fileUploadErrors?: string[];
  loading: boolean;
  faqs: QuestionsAndAnswersType[];
  sendFile: (file: File | null) => void;
  fileName: string | null;
  setFileName: Dispatch<SetStateAction<string | null>>;
}

export const FileReportingUploadPanel = ({
  fileResponse,
  fileUploadErrors,
  loading,
  faqs,
  sendFile,
  fileName,
  setFileName,
}: FileReportingUploadPanelProps) => {
  const dispatch = useAppDispatch();
  const { fileUploadPanelState, reviewAndConfirmPanelState } = useAppSelector(state => state.file);
  const { t } = useTypedTranslation();

  const selectedPartnerNumberOrganization = useAppSelector(state => state.auth.user?.selectedPartnerNumberOrganization);

  const disableNextButton =
    loading ||
    !fileName ||
    fileResponse?.fileReportStatus === FileReportValidationStatusEnum.WITH_ERRORS ||
    fileResponse === null;

  const goBackToFileUpload = () => dispatch(goToFileUploadPanel());

  return (
    <Panel
      {...fileUploadPanelState}
      name="panel-file-upload"
      onToggle={reviewAndConfirmPanelState.status === PanelStatus.Active ? goBackToFileUpload : undefined}
    >
      <Panel.Header>{t('panel.fileUpload.panelTitle', { fileServiceName: 'Filuppladdning' })}</Panel.Header>
      <Panel.Body>
        <Panel.MainSection>
          <Flex maxWidth={790} flexDirection={'column'} rowGap={60}>
            <Text>
              {selectedPartnerNumberOrganization
                ? t('panel.fileUpload.introText')
                : t('panel.fileUpload.introTextMultiCompany')}
            </Text>
            <FileDrop
              types={!selectedPartnerNumberOrganization ? ['json'] : ['json', 'xlsx', 'xls']}
              loading={loading}
              sendFile={sendFile}
              fileName={fileName}
              setFileName={setFileName}
            />
          </Flex>
          <FileReportStatusCard fileReport={fileResponse} />

          {fileUploadErrors && fileUploadErrors.length > 0 && (
            <Feedback type="error" negativeSideMargins={true} marginBottom={40}>
              {fileUploadErrors.map((errorMessage, ind) => (
                <Text key={ind} size="small">
                  {errorMessage}
                </Text>
              ))}
            </Feedback>
          )}

          <Button
            disabled={disableNextButton}
            variant={disableNextButton ? 'grey' : 'primary'}
            onClick={() => dispatch(goToReviewAndConfirmPanel())}
            color={disableNextButton ? 'black70' : 'white'}
          >
            {t('common.nextStep')}
          </Button>
        </Panel.MainSection>

        <Panel.AsideSection>{renderAsideSection(faqs)}</Panel.AsideSection>
      </Panel.Body>
    </Panel>
  );
};

const renderAsideSection = (faqs: QuestionsAndAnswersType[]) => (
  <FAQ testId="test-storybook-faq" layout="side" questionAnswers={faqs} />
);
